// "Hold the bags" (sprint 36 item 3): Mr Holden's mini-game. A soft bag swings in on a hook from a side; tap to drop it onto the pile in his arms.
// Shared by the page and the server replay. Integer pixels in the 180 x 300 frame (the pile's centre is 0), a 256-entry integer sine table,
// no floats and no Math.sin/pow/exp; all randomness is read from the seed bytes (one per step), never from a clock.
// The soft bag does not shave the pile narrower: the part beyond the bag below spills off (drawn) and the pile's top becomes the landed
// bag's own span, so a miss shifts the pile and the next bag is measured against that. More than TUNING.tip % of a bag off one side tips the pile.
export const id = 'holden';
export const STEPS = 120;
export const NAME = 'Hold the bags';

// The tuning numbers (the designer's), all integers:
//  A:     the swing's half-width in px (the rope frames reach 24); early / late: the steps of one full swing for bags 2 to 4 and 5 to 7
//         (the sack takes what is left of the 120 steps, 21 by default); tip: the share of a bag, in percent, that may hang off one side;
//         clean: a bag is clean when no more than 1 / clean of its width hangs off (3: a third; sprint 36 designer, was a quarter).
export const TUNING = { A: 22, early: 18, late: 11, tip: 60, clean: 3 };

export const WIDTH = { paper: 22, tote: 16, sack: 28 };
export const LAND = { paper: 14, tote: 11, sack: 26 }; // the rows a landed bag adds to the pile (client/art/aseprite/holden-game/holden-game-anchors.json)
export const LINE = 6; // the rescue is won when this many bags are on the pile
const FIRST = 12; // bag 1 lowers and drops itself in the first 12 steps, centred: the teaching drop

// 1000 x sin(2 pi i / 256), rounded
export const SIN = [
  0, 25, 49, 74, 98, 122, 147, 171, 195, 219, 243, 267, 290, 314, 337, 360,
  383, 405, 428, 450, 471, 493, 514, 535, 556, 576, 596, 615, 634, 653, 672, 690,
  707, 724, 741, 757, 773, 788, 803, 818, 831, 845, 858, 870, 882, 893, 904, 914,
  924, 933, 942, 950, 957, 964, 970, 976, 981, 985, 989, 992, 995, 997, 999, 1000,
  1000, 1000, 999, 997, 995, 992, 989, 985, 981, 976, 970, 964, 957, 950, 942, 933,
  924, 914, 904, 893, 882, 870, 858, 845, 831, 818, 803, 788, 773, 757, 741, 724,
  707, 690, 672, 653, 634, 615, 596, 576, 556, 535, 514, 493, 471, 450, 428, 405,
  383, 360, 337, 314, 290, 267, 243, 219, 195, 171, 147, 122, 98, 74, 49, 25,
  0, -25, -49, -74, -98, -122, -147, -171, -195, -219, -243, -267, -290, -314, -337, -360,
  -383, -405, -428, -450, -471, -493, -514, -535, -556, -576, -596, -615, -634, -653, -672, -690,
  -707, -724, -741, -757, -773, -788, -803, -818, -831, -845, -858, -870, -882, -893, -904, -914,
  -924, -933, -942, -950, -957, -964, -970, -976, -981, -985, -989, -992, -995, -997, -999, -1000,
  -1000, -1000, -999, -997, -995, -992, -989, -985, -981, -976, -970, -964, -957, -950, -942, -933,
  -924, -914, -904, -893, -882, -870, -858, -845, -831, -818, -803, -788, -773, -757, -741, -724,
  -707, -690, -672, -653, -634, -615, -596, -576, -556, -535, -514, -493, -471, -450, -428, -405,
  -383, -360, -337, -314, -290, -267, -243, -219, -195, -171, -147, -122, -98, -74, -49, -25,
];
const cos = i => SIN[(i + 64) & 255];

// Where a bag of width w centred at c lands on a pile whose top spans [top.l, top.r): a wider bag counts the support as widened by half
// the difference on each side. off: px beyond the support on its worse side; quarter: no more than a quarter of the bag's width.
export function landing(top, c, w) {
  const h = w >> 1, sw = top.r - top.l, widen = w > sw ? (w - sw) >> 1 : 0;
  const left = Math.max(0, top.l - widen - (c - h)), right = Math.max(0, c + h - (top.r + widen));
  const off = Math.max(left, right);
  return { off, side: off === 0 ? 0 : right >= left ? 1 : -1, tip: off * 100 > TUNING.tip * w, spill: off > 0, clean: off * TUNING.clean <= w };
}

// The hook's x (px from the pile's centre) at step u of a bag's window: it enters at an extreme, crosses the middle a quarter of the way
// through, is at the far extreme at half time and is back near its edge on the window's last step, where an untouched bag drops by itself.
const xAt = (w, u) => (w.auto ? 0 : Math.trunc((w.side * TUNING.A * cos(Math.floor((u * 256) / w.period))) / 1000));

function windows(seed) {
  const totes = seed[0] & 1 ? [2, 5] : [3, 6];
  const out = [];
  let start = 0;
  for (let bag = 1; bag <= 8; bag++) {
    const period = bag === 1 ? FIRST : bag <= 4 ? TUNING.early : bag <= 7 ? TUNING.late : STEPS - start;
    const kind = bag === 8 ? 'sack' : totes.includes(bag) ? 'tote' : 'paper';
    out.push({ bag, start, end: start + period - 1, period, kind, w: WIDTH[kind], side: bag === 1 ? 0 : seed[start] & 1 ? 1 : -1, auto: bag === 1 });
    start += period;
  }
  return out;
}

export function init(seed, { mode = 'bonus' } = {}) {
  return { t: 0, mode, windows: windows(seed), drops: 0, top: { l: -(WIDTH.paper >> 1), r: WIDTH.paper >> 1 }, landed: [], tip: null, lineStep: -1, clean: true, over: false };
}

const current = s => {
  if (s.over) return null;
  const w = s.windows.find(x => s.t >= x.start && s.t <= x.end);
  return w && w.bag === s.drops + 1 ? w : null;
};

// What a player could see: the bag on the hook with its whole path (the swing is predictable), the pile, and the rescue line.
export function observe(s) {
  const w = current(s);
  return {
    t: s.t, A: TUNING.A, line: LINE, lineRows: s.windows.slice(0, LINE).reduce((a, x) => a + LAND[x.kind], 0), lineStep: s.lineStep,
    windows: s.windows.map(({ bag, start, end, period, kind, w: width, side, auto }) => ({ bag, start, end, period, kind, w: width, side, auto })),
    hang: w ? { bag: w.bag, kind: w.kind, w: w.w, start: w.start, end: w.end, period: w.period, side: w.side, auto: w.auto, tappable: !w.auto, u: s.t - w.start, x: xAt(w, s.t - w.start),
      path: Array.from({ length: w.period }, (_, u) => xAt(w, u)) } : null,
    landed: s.landed.map(b => ({ ...b })), top: { ...s.top }, bags: s.landed.length, tip: s.tip ? { ...s.tip } : null,
  };
}

function drop(s, w) {
  const c = xAt(w, s.t - w.start), a = landing(s.top, c, w.w), h = w.w >> 1;
  s.drops += 1;
  if (a.tip) { s.tip = { bag: w.bag, kind: w.kind, w: w.w, c, off: a.off, side: a.side, step: s.t }; s.clean = false; s.over = true; return; }
  s.landed.push({ bag: w.bag, kind: w.kind, w: w.w, c, off: a.off, side: a.side, step: s.t });
  s.top = { l: c - h, r: c + h };
  if (!a.clean) s.clean = false;
  if (s.lineStep < 0 && s.landed.length >= LINE) s.lineStep = s.t;
}

// input: 1 is a tap, which drops the hanging bag now; 0 is nothing. The first bag and a window's last step drop by themselves.
export function step(s, input) {
  if (s.over) return s;
  const w = current(s);
  if (w && (s.t === w.end || (!w.auto && Number(input) === 1))) drop(s, w);
  s.t += 1;
  if (!s.over && s.t >= STEPS) s.over = true;
  return s;
}

// bonus: reachedEnd is a pile still standing at the bell; rescue: it is the line (bag 6 landed), which stays won if a later bag tips.
// bags: bags on the pile (the tipping bag is not one); tippedBag: the bag that tipped it, 0 for none.
export const result = s => ({ over: s.over, steps: s.t, reachedEnd: s.mode === 'rescue' ? s.lineStep >= 0 : s.over && !s.tip, clean: s.clean, bags: s.landed.length, tippedBag: s.tip ? s.tip.bag : 0 });
